import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { Supplier, User } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { Amount } from "models/utilTypes/currencyType";
import { IncomingInvoiceListViewProps } from "models/propTypes/incomingInvoicePropTypes";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { TableAsync, useFilterContext, CurrencyValueFormatter } from "components/common";
import IncomingInvoiceStatusTags from "./statusTags";
import IncomingInvoicePaymentStatus from "./paymentStatus";
import { useIncomingInvoiceService } from "serviceHooks";
//#endregion
import { CurrencyHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const IncomingInvoiceListView: React.ForwardRefRenderFunction<TableHandles, IncomingInvoiceListViewProps> = (
  props,
  ref
) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const { filter } = useFilterContext<IncomingInvoiceFormTypes.Filter>();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => incomingInvoiceService.reportFilter(baseFilter, filter),
    [filter]
  );

  const getItemUrl = (id: number | string) => UrlHelper.formatPath(`/IncomingInvoice/${id}`);

  const isUnhandled = (state: WintSuperkollAPIModel.IncomingInvoiceState) =>
    state === WintSuperkollAPIModel.IncomingInvoiceState.Unhandled ||
    state === WintSuperkollAPIModel.IncomingInvoiceState.SentForProcessing;

  return (
    <TableAsync<WintSuperkollAPIModel.IncomingInvoice>
      tableRef={ref}
      properties={{
        certifyPersonName: {
          description: TranslationKeys.Common.Owner,
          helpText: TranslationKeys.IncomingInvoice.ReceiverHelpText,
          formatter: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            values.certifyPersonId ? new User(values.certifyPersonId, values.certifyPersonName, true) : "",
          noWrap: true,
          alignment: "center",
        },
        invoiceDate: {
          description: TranslationKeys.Common.InvoiceDate,
          helpText: TranslationKeys.IncomingInvoice.SupplierInvoiceDateHelpText,
          formatter: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            isUnhandled(values.state) ? "" : new Date(values.invoiceDate),
          noWrap: true,
          maxWidth: 130,
        },
        dueDate: {
          description: TranslationKeys.Common.Due_Date,
          helpText: TranslationKeys.IncomingInvoice.SupplierDueDateHelpText,
          formatter: (values: WintSuperkollAPIModel.IncomingInvoicePut) =>
            values.dueDate === undefined ? "" : new Date(values.dueDate),
          noWrap: true,
          maxWidth: 130,
        },
        supplierName: {
          description: TranslationKeys.IncomingInvoice.Supplier,
          helpText: TranslationKeys.IncomingInvoice.SupplierNameHelpText,
          formatter: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            new Supplier(values.supplier?.id ?? 0, values.supplier?.name ?? ""),
          noWrap: true,
          maxWidth: 220,
        },
        oCR: {
          description: TranslationKeys.Common.Number,
          helpText: TranslationKeys.IncomingInvoice.InvoiceNoHelpText,
          noWrap: true,
          classification: "number",
        },
        amountWithSign: {
          description: TranslationKeys.Common.TotalAmount,
          descriptionToPrint: `${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.Amount
          )} (${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InclVAT)})`,
          helpText: TranslationKeys.IncomingInvoice.SupplierAmountInclVATHelpText,
          alignment: "right",
          formatter: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            isUnhandled(values.state) && values.amountWithSign === 0
              ? ""
              : new Amount(values.currency, values.amountWithSign),
          summaryValueFormatter: (values: WintSuperkollAPIModel.IncomingInvoice) => (
            <CurrencyValueFormatter sumValue={values.totalAmountSummarySEK} cellValue={values.totalAmountSummary} />
          ),
          summaryToPrint: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            CurrencyHelper.getSummaryToPrintWithSum(values.totalAmountSummarySEK, values.totalAmountSummary),
          noWrap: true,
          classification: "amount",
        },
        amountExcludingTax: {
          description: TranslationKeys.Common.AmountExVat2,
          helpText: TranslationKeys.Common.AmountExVatHelpText,
          formatter: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            isUnhandled(values.state) && values.amountExcludingTax === 0
              ? ""
              : new Amount(values.currency, values.amountExcludingTax),
          summaryValueFormatter: (values: WintSuperkollAPIModel.IncomingInvoice) => (
            <CurrencyValueFormatter
              sumValue={values.amountExcludingTaxSummarySEK}
              cellValue={values.amountExcludingTaxSummary}
            />
          ),
          summaryToPrint: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            CurrencyHelper.getSummaryToPrintWithSum(
              values.amountExcludingTaxSummarySEK,
              values.amountExcludingTaxSummary
            ),
          alignment: "right",
          noWrap: true,
          classification: "amount",
        },
        leftToPay: {
          description: TranslationKeys.Common.Unpaid2,
          helpText: TranslationKeys.IncomingInvoice.LeftToPayHelpText,
          alignment: "right",
          formatter: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            isUnhandled(values.state) && values.leftToPay === 0 ? "" : new Amount(values.currency, values.leftToPay),
          summaryValueFormatter: (values: WintSuperkollAPIModel.IncomingInvoice) => (
            <CurrencyValueFormatter sumValue={values.amountLeftSummarySEK} cellValue={values.amountLeftSummary} />
          ),
          summaryToPrint: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            CurrencyHelper.getSummaryToPrintWithSum(values.amountLeftSummarySEK, values.amountLeftSummary),
          className: cssExports.textBlack,
          noWrap: true,
          classification: "amount",
        },
        state: {
          description: TranslationKeys.Common.Status,
          helpText: TranslationKeys.IncomingInvoice.StatusHelpText,
          formatter: (values: WintSuperkollAPIModel.IncomingInvoice) => (
            <IncomingInvoiceStatusTags status={values.state} />
          ),
          valueToPrint: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            incomingInvoiceService.getStateText(incomingInvoiceService.toViewState(values.state)),
          maxWidth: 120,
        },
        paymentStatus: {
          description: TranslationKeys.Common.Paid2,
          helpText: TranslationKeys.IncomingInvoice.PaidHelpText,
          alignment: "center",
          formatter: (values: WintSuperkollAPIModel.IncomingInvoice) => (
            <IncomingInvoicePaymentStatus status={values.paymentStatus} />
          ),
          valueToPrint: (values: WintSuperkollAPIModel.IncomingInvoice) =>
            incomingInvoiceService.getPaymentStateText(values.paymentStatus),
          maxWidth: 80,
        },
      }}
      filter={filterFunc}
      pagination="advanced"
      includeSummary
      maxRowCount={10}
      itemUrl={getItemUrl}
      orderByProperty="invoiceDate"
      orderByDescending
      reloadKey={props.reloadKey}
      exportFileName={
        props.exportFileName
          ? props.exportFileName
          : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.IncomingInvoices)
      }
    />
  );
};
export default React.forwardRef(IncomingInvoiceListView);
